import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  Brush
} from 'recharts';
import constants from '@shared-styles/constants.module.scss';
import GraphTooltip from './GraphTooltip';
import NotEnoughDataMessage from '@shared/components/NotEnoughDataMessage';
import { CLASSIFICATION_CALCULATION_TYPE } from '../constants/classificationCalculationType';
import { itemSorterFn } from '../../../utils/chartUtils';

const LEGEND_ORDER = {
  average: 1,
  practice: 2
};

/**
 * @typedef {object} DataCorrelationGraphData
 * @property {string} quarter
 * @property {number} [average]
 * @property {number} [practice]
 */

/**
 * @typedef {object} DataCorrelationGraphProps
 * @property {DataCorrelationGraphData[]} [data]
 * @property {string} [practiceLabel]
 * @property {string} [metricLabel]
 * @property {number} [metricCalculationType]
 */

/**
 * @param {DataCorrelationGraphProps} props
 */
const DataCorrelationGraph = props => {
  const { data, practiceLabel, metricLabel, metricCalculationType } = props;

  if (!data.length) {
    return <NotEnoughDataMessage />;
  }

  const isPercentage = metricCalculationType === CLASSIFICATION_CALCULATION_TYPE.PERCENTAGE;
  const isTotal = metricCalculationType === CLASSIFICATION_CALCULATION_TYPE.TOTAL;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={data}>
        {!isTotal && <CartesianGrid strokeDasharray='3 3' />}
        {isTotal && (
          <>
            <CartesianGrid strokeDasharray='3 3' yAxisId='left' />
            <CartesianGrid strokeDasharray='3 3' yAxisId='right' />
          </>
        )}
        <XAxis dataKey='quarter' tickMargin={8} />

        {isPercentage && <YAxis domain={[0, 100]} tickFormatter={tick => `${tick}%`} />}

        {isTotal && (
          <>
            <YAxis yAxisId='left' orientation='left' stroke={constants.isacaMediumBlue} />

            <YAxis
              yAxisId='right'
              orientation='right'
              domain={[0, 100]}
              tickFormatter={tick => `${tick}%`}
              stroke={constants.isacaOrange}
            />
          </>
        )}

        <Tooltip content={<GraphTooltip />} />
        <Legend itemSorter={itemSorterFn(LEGEND_ORDER)} />

        <Line
          dataKey='average'
          stroke={constants.isacaMediumBlue}
          strokeWidth={2}
          name={metricLabel}
          dot={{ strokeWidth: 2 }}
          {...(isTotal && { yAxisId: 'left' })}
        />

        <Line
          dataKey='practice'
          stroke={constants.isacaOrange}
          strokeWidth={2}
          name={practiceLabel}
          dot={{ strokeWidth: 2 }}
          {...(isTotal && { yAxisId: 'right' })}
        />

        <Brush
          dataKey='quarter'
          stroke={constants.isacaMediumBlue}
          fill={constants.isacaMediumBlueWithLightOpacity}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default DataCorrelationGraph;
